import {
	ClientContact,
	ClientContactRequest,
	ClientUserContactType
} from "@/Contracts";
import ZDynamicSelect from "@molecules/ZDynamicSelect";
import ZMessage from "@molecules/ZMessage";
import { getClientContacts as getClientContactsService } from "@/Services/Clients";
import React, { useEffect, useState } from "react";
import ZTypography from "@atoms/ZTypography";
import { AddClientContactDrawer } from "../../../Clients/ClientContact/Add";
import ClientsActionCreators from "@/Actions/Clients";
import useDispatch from "@/Components/Utils/useDispatch";
import OpportunitiesActionCreators from "@/Actions/Opportunity";
import { shouldShowAddEntityActions } from "@/Utils";

const ContactSelector = ({
	value,
	onChange,
	onSelect,
	fieldId,
	selectedClient,
	selectedPrimaryClientContact,
	selectedLinkedClientContacts,
	isOnboardingClientSelected
}: {
	value?: string;
	onChange: (value?: string | string[]) => void;
	onSelect?: (value?: string) => void;
	fieldId?: "primary_client_user_id" | "linked_client_user_ids";
	selectedClient?: string;
	selectedPrimaryClientContact?: string;
	selectedLinkedClientContacts?: string[];
	isOnboardingClientSelected?: boolean;
}) => {
	const [loading, setLoading] = useState(false);
	useEffect(() => {
		if (fieldId === "linked_client_user_ids") fetchLinkedContacts("");
		else fetchPrimaryContacts("");
	}, []);
	const [primaryContactList, setPrimaryContactList] = useState<
		ClientContact[]
	>([]);
	const dispatch = useDispatch();
	const getClientContactsDispatch = (payload: ClientContactRequest) =>
		dispatch(ClientsActionCreators.getClientContacts(payload));
	const showOpportunitiesLoaderDispatch = () =>
		dispatch(OpportunitiesActionCreators.showOpportunitiesLoader());
	const hideOpportunitiesLoaderDispatch = () =>
		dispatch(OpportunitiesActionCreators.hideOpportunitiesLoader());
	const [contactList, setContactList] = useState<ClientContact[]>([]);
	const [addClientContactDrawerOpen, setAddClientContactDrawerOpen] =
		useState<boolean>(false);
	const [defaultClientUserContactType, setDefaultClientUserContactType] =
		useState<ClientUserContactType>(ClientUserContactType.SecondaryContact);
	const fetchPrimaryContacts = async (value: string) => {
		if (!selectedClient) {
			ZMessage.error("Please select a client");
			return;
		}
		const clientContactRequest = new ClientContactRequest(selectedClient);
		clientContactRequest.keywords = value;
		if (!value) {
			clientContactRequest.selected_id = selectedPrimaryClientContact
				? [selectedPrimaryClientContact]
				: [];
		}
		try {
			setLoading(true);
			const response = await getClientContactsService(
				clientContactRequest
			);
			// directly calling service, no need to change redux state

			setPrimaryContactList(response.data);
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const showAddClientContactDrawer = () => {
		if (fieldId == "linked_client_user_ids") {
			setDefaultClientUserContactType(
				ClientUserContactType.SecondaryContact
			);
		} else {
			setDefaultClientUserContactType(
				ClientUserContactType.PrimaryContact
			);
		}
		if (selectedClient) {
			setAddClientContactDrawerOpen(true);
		} else {
			ZMessage.error("Please select a Client first");
		}
	};

	const closeAddClientContactDrawer = () => {
		if (!selectedClient) {
			return;
		}
		const clientContactRequest = new ClientContactRequest(selectedClient);
		getClientContactsDispatch(clientContactRequest);
		setAddClientContactDrawerOpen(false);
	};

	const fetchLinkedContacts = async (value: string): Promise<void> => {
		if (!selectedClient) {
			ZMessage.error("Please select a client");
			return;
		}
		const clientContactRequest = new ClientContactRequest(selectedClient);

		clientContactRequest.keywords = value;

		if (!value) {
			clientContactRequest.selected_id = selectedLinkedClientContacts;
		}

		try {
			setLoading(true);
			const response = await getClientContactsService(
				clientContactRequest
			);
			// directly calling service, no need to change redux state

			setContactList(response.data);
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	let options: { name: string; value: string }[] = [];
	if (fieldId === "linked_client_user_ids") {
		options = contactList
			.filter(contact => contact?.id !== selectedPrimaryClientContact)
			.map(contact => ({
				name: `${contact.name.full} ${
					contact.email ? `<${contact.email}>` : ""
				}`,
				value: contact?.id
			}));
	} else {
		options = primaryContactList
			.filter(
				contact => !selectedLinkedClientContacts?.includes(contact?.id)
			)
			.map(contact => ({
				name: `${contact.name.full} ${
					contact.email ? `<${contact.email}>` : ""
				}`,
				value: contact?.id
			}));
	}
	const getClientContactsData = async (selectedClient: string) => {
		if (!selectedClient) {
			return;
		}
		showOpportunitiesLoaderDispatch();
		try {
			const clientContactRequest = new ClientContactRequest(
				selectedClient
			);
			const response = await getClientContactsDispatch(
				clientContactRequest
			);
			setContactList(response?.clientContactList);
			setPrimaryContactList(
				response?.clientContactList.sort((a, _b) =>
					a.contact_type === ClientUserContactType.PrimaryContact
						? -1
						: 1
				)
			);
		} catch (error) {
			console.error(error);
		} finally {
			hideOpportunitiesLoaderDispatch();
		}
	};
	const handleAddNewClientContactCallback = async (
		clientContact: ClientContact
	) => {
		await getClientContactsData(clientContact.client_company_id);
		if (
			clientContact.contact_type == ClientUserContactType.PrimaryContact
		) {
			onChange(clientContact.id);
		} else {
			onChange(
				(selectedLinkedClientContacts ?? []).concat(clientContact.id)
			);
		}
	};

	return (
		<>
			<ZDynamicSelect
				loading={loading}
				disabled={isOnboardingClientSelected}
				fetchData={
					fieldId === "linked_client_user_ids"
						? fetchLinkedContacts
						: fetchPrimaryContacts
				}
				placeholder={
					fieldId === "linked_client_user_ids"
						? "Please choose linked contacts"
						: "Please choose opportunity primary contact"
				}
				allowClear={true}
				options={options}
				shouldFetchDefaultData
				isMultiSelect={fieldId === "linked_client_user_ids"}
				onBlur={
					fieldId === "linked_client_user_ids"
						? () => fetchLinkedContacts("")
						: undefined
				}
				value={value}
				onChange={onChange}
				onSelect={onSelect}
				dropdownRender={menu => (
					<div>
						{menu}
						{shouldShowAddEntityActions() && (
							<div
								className="add-entity-action"
								onMouseDown={$event => {
									$event.preventDefault();
								}}
								onClick={showAddClientContactDrawer}
							>
								<ZTypography.ZText className="text-blue-6">
									Add Contact
								</ZTypography.ZText>
							</div>
						)}
					</div>
				)}
			/>
			<AddClientContactDrawer
				clientId={selectedClient ?? ""}
				visible={addClientContactDrawerOpen}
				onClose={closeAddClientContactDrawer}
				activeTabContactType={defaultClientUserContactType}
				onSubmit={handleAddNewClientContactCallback}
			/>
		</>
	);
};

export default ContactSelector;
